import { useState, useRef, useCallback, useEffect } from 'react';
import { Volume2, VolumeX, Loader2, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const NATURAL_VOICE_ID = 'FGY2WhTYpPnrIDTdsKH5'; // Laura (warm, natural)

interface ListenButtonProps {
  message: string;
  className?: string;
  size?: 'default' | 'sm' | 'lg';
  /** Public announcement slug — proves a legitimate public view context */
  announcementSlug?: string;
  /** Recipient view token — proves a legitimate recipient context */
  recipientToken?: string;
}


/** Pick the best available browser voice for fallback */
function pickBestBrowserVoice(): SpeechSynthesisVoice | null {
  const voices = speechSynthesis.getVoices();
  if (!voices.length) return null;

  // Preferred warm/natural voices ranked
  const preferred = ['Samantha', 'Karen', 'Moira', 'Google UK English Female', 'Microsoft Zira'];
  for (const name of preferred) {
    const match = voices.find(v => v.name.includes(name));
    if (match) return match;
  }
  // Fallback: first female-sounding English voice
  const english = voices.filter(v => v.lang.startsWith('en'));
  return english[0] || voices[0];
}

export function ListenButton({ message, className, size = 'default', announcementSlug, recipientToken }: ListenButtonProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'paused'>('idle');
  const [usingFallback, setUsingFallback] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const cleanup = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    if (utteranceRef.current) {
      speechSynthesis.cancel();
      utteranceRef.current = null;
    }
    setUsingFallback(false);
  }, []);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const playBrowserFallback = useCallback((text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = pickBestBrowserVoice();
    if (voice) utterance.voice = voice;
    utterance.rate = 0.9;
    utterance.pitch = 1.02;
    utterance.volume = 1;
    utteranceRef.current = utterance;
    setUsingFallback(true);

    utterance.onend = () => { cleanup(); setState('idle'); };
    utterance.onerror = () => { cleanup(); setState('idle'); };

    speechSynthesis.speak(utterance);
    setState('playing');
  }, [cleanup]);

  const handlePlay = useCallback(async () => {
    // Resume from pause (premium audio)
    if (audioRef.current && state === 'paused') {
      await audioRef.current.play();
      setState('playing');
      return;
    }
    // Resume from pause (browser fallback)
    if (utteranceRef.current && state === 'paused') {
      speechSynthesis.resume();
      setState('playing');
      return;
    }
    // Pause
    if (state === 'playing') {
      if (audioRef.current) audioRef.current.pause();
      if (utteranceRef.current) speechSynthesis.pause();
      setState('paused');
      return;
    }

    const normalized = message.trim();
    if (!normalized) {
      toast.error('Nothing to read yet.');
      return;
    }

    cleanup();
    setState('loading');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const response = await fetch(`${SUPABASE_URL}/functions/v1/elevenlabs-tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${accessToken || SUPABASE_KEY}`,
        },
        body: JSON.stringify({
          text: normalized,
          voiceId: NATURAL_VOICE_ID,
          announcementSlug,
          recipientToken,
        }),
      });


      if (!response.ok) {
        throw new Error('elevenlabs_failed');
      }

      const audioBlob = await response.blob();
      if (audioBlob.size === 0) throw new Error('empty_audio_response');

      const audioUrl = URL.createObjectURL(audioBlob);
      audioUrlRef.current = audioUrl;

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.addEventListener('ended', () => { cleanup(); setState('idle'); });
      audio.addEventListener('error', () => { cleanup(); setState('idle'); toast.error('Audio playback failed.'); });

      await audio.play();
      setState('playing');
    } catch {
      // Fallback to browser voice
      cleanup();
      playBrowserFallback(normalized);
    }
  }, [cleanup, message, state, playBrowserFallback]);

  const handleStop = useCallback(() => {
    cleanup();
    setState('idle');
  }, [cleanup]);

  const icon = (() => {
    switch (state) {
      case 'loading': return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'playing': return <Pause className="h-4 w-4" />;
      case 'paused': return <Play className="h-4 w-4" />;
      default: return <Volume2 className="h-4 w-4" />;
    }
  })();

  const label = (() => {
    switch (state) {
      case 'loading': return 'Loading...';
      case 'playing': return 'Pause';
      case 'paused': return 'Resume';
      default: return 'Listen';
    }
  })();

  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      <Button
        variant="outline"
        size={size}
        onClick={handlePlay}
        disabled={state === 'loading'}
        className="gap-2"
        title="Listen to this message read aloud"
      >
        {icon}
        <span>{label}</span>
      </Button>
      {(state === 'playing' || state === 'paused') && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleStop}
          className="h-8 w-8"
          title="Stop"
        >
          <VolumeX className="h-4 w-4" />
        </Button>
      )}
      {usingFallback && state === 'playing' && (
        <span className="text-xs text-muted-foreground ml-1">Using device voice</span>
      )}
    </div>
  );
}
